/**
 * Dialog State Provider for Update Study Structure
 *
 * Migrated from: lego/page/etmf/overview/update-study/context/DialogState.jsx
 */

import React, { useEffect, useRef, useState, ReactNode } from 'react'
import {
  DialogContext,
  type FormValues,
  type DialogContextValue,
  type SelectOption,
  type ActionOptions,
  type LevelOptions
} from './DialogContext'
import { useServiceContext } from '../../../../../root'
import { useEsignature } from '../../../../../hooks/useEsignature'
import { OptionType } from '../../../../../components/Select/Select'
import { MasterDataType } from '../../../../../constants/etmf'
import _ from 'lodash'

interface DialogStateProps {
  closeModal: () => void
  children: ReactNode
}

export function DialogState({ closeModal, children }: DialogStateProps) {
  const context = useServiceContext()
  const { service, api, constant } = context
  const { translate } = context.service.filter
  const { signAction } = useEsignature()

  // Dialog properties
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [currentReq, setCurrentReq] = useState<Promise<any> | null>(null)
  const [title, setTitle] = useState<string>('')

  // Options - using SelectOption to match DialogContextValue interface
  const [productOptions, setProductOptions] = useState<SelectOption[]>([])
  const [studyOptions, setStudyOptions] = useState<SelectOption[]>([])
  const [environmentOptions, setEnvironmentOptions] = useState<SelectOption[]>([])
  const [countryOptions, setCountryOptions] = useState<any[]>([])
  const [siteOptions, setSiteOptions] = useState<any[]>([])

  const [selection, setSelection] = useState<any>({
    product: undefined,
    study: undefined,
    environment: undefined,
    countries: [],
    sites: []
  })

  const studyOptionsCached = useRef<Record<string, SelectOption[]>>({})
  const environmentOptionsCached = useRef<Record<string, SelectOption[]>>({})
  const countryOptionsCached = useRef<Record<string, any[]>>({})
  const siteOptionsCached = useRef<Record<string, any[]>>({})

  const actionOptions = {
    add: 'add',
    remove: 'remove'
  }

  const levelOptions = {
    sites: 'sites',
    countries: 'countries'
  }

  // Values
  const defaultValues: FormValues = {
    action: actionOptions.add,
    level: levelOptions.countries,
    product: null,
    study: null,
    environment: null,
    countries: [],
    sites: []
  }

  const values = useRef<FormValues>(defaultValues)

  useEffect(() => {
    updateTitle()
    loadProducts()
  }, [])

  useEffect(() => {
    updateTitle()
  }, [currentStep])

  useEffect(() => {
    if (currentReq) {
      setIsLoading(true)
      const req = currentReq

      currentReq
        .then(() => {
          if (req === currentReq) {
            setIsLoading(false)
          }
        })
        .catch(() => {
          if (req === currentReq) {
            setIsLoading(false)
          }
        })
    }
  }, [currentReq])

  function updateTitle() {
    const titleBase = 'Update Study Structure'
    let subtitle = ''

    switch (currentStep) {
      case 1:
        subtitle = ' - Select Action'
        break
      case 2:
        subtitle =
          values.current.action === actionOptions.add ? ' - Select Item(s) to Add' : ' - Select Item(s) to Remove'
        break
      case 3:
        if (values.current.level === levelOptions.countries) {
          subtitle =
            values.current.action === actionOptions.add
              ? ' - Add Country(ies) to Study'
              : ' - Remove Country(ies) from Study'
        } else {
          subtitle =
            values.current.action === actionOptions.add ? ' - Add Site(s) to Study' : ' - Remove Site(s) from Study'
        }
        break
    }

    setTitle(titleBase + subtitle)
  }

  function loadProducts() {
    return service.etmf.queryEtmfStructure(constant.etmf.masterData.type.PRODUCT).then((data: any[]) => {
      const ops = data
        .map((item) => ({
          id: item.fsiId,
          value: item.fsiId,
          label: item.fsiName,
          name: item.fsiName
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
      setProductOptions(ops)
    })
  }

  function loadStudyOptions(): Promise<void> {
    const cachedOptions = studyOptionsCached.current[values.current.product!]
    if (cachedOptions) {
      setStudyOptions(cachedOptions)
      return Promise.resolve()
    } else {
      setStudyOptions([])

      if (values.current.product) {
        const product = productOptions.find((p) => p.value === values.current.product)
        const req = service.etmf
          .queryEtmfStructure(constant.etmf.masterData.type.TRIAL, {
            product: { fsiName: product?.label || '' }
          })
          .then((data: any[]) => {
            const ops = data
              .map((item) => ({
                id: item.fsiId,
                value: item.fsiId,
                label: item.fsiName,
                name: item.fsiName
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
            studyOptionsCached.current[values.current.product!] = ops
            setStudyOptions(ops)
          })
        setCurrentReq(req)
        return req
      }
    }
    return Promise.resolve()
  }

  function loadEnvironmentOptions(): Promise<void> {
    const cachedOptions = environmentOptionsCached.current[values.current.study!]
    if (cachedOptions) {
      setEnvironmentOptions(cachedOptions)
      return Promise.resolve()
    } else {
      setEnvironmentOptions([])

      if (values.current.study) {
        setIsLoading(true)
        const product = productOptions.find((p) => p.value === values.current.product)
        const study = studyOptions.find((s) => s.value === values.current.study)

        const req = service.etmf
          .queryEtmfStructure(constant.etmf.masterData.type.ENVIRONMENT, {
            product: { fsiName: product?.label || '' },
            trial: { fsiName: study?.label || '' }
          })
          .then((data: any[]) => {
            const ops = data
              .map((item) => ({
                id: item.fsiId,
                value: item.fsiId,
                label: item.fsiName,
                name: item.fsiName
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
            environmentOptionsCached.current[values.current.study!] = ops
            setEnvironmentOptions(ops)
          })
        setCurrentReq(req)
        return req
      }
    }
    return Promise.resolve()
  }

  function loadCountryOptions(params: { sortDesc: boolean }): Promise<{
    elements: SelectOption[]
    totalElements: number
    isCached: boolean
  }> {
    return new Promise((resolve) => {
      const environmentId = values.current.environment
      if (!environmentId) {
        resolve({ elements: [], totalElements: 0, isCached: false })
        return
      }

      const cachedOptions = countryOptionsCached.current[environmentId]

      if (cachedOptions) {
        const opts = sortArray(cachedOptions, params.sortDesc)
        resolve({
          elements: opts,
          totalElements: opts.length,
          isCached: true
        })
      } else {
        const study = studyOptions.find((s) => s.value === values.current.study)
        const environment = environmentOptions.find((e) => e.value === environmentId)

        service.etmf
          .getFilterParamsForMdQuery(constant.etmf.masterData.type.COUNTRY, {
            trial: { fsiName: study?.label || '' },
            environment: { fsiName: environment?.label || '' }
          })
          .then((filterParams: any) => {
            api.etmf
              .getCountryMDsForUpdate(environmentId, filterParams, values.current.action === actionOptions.add, true)
              .then((data: any[]) => {
                const opts = sortArray(data, params.sortDesc)
                countryOptionsCached.current[environmentId] = opts
                setCountryOptions(opts)
                resolve({
                  elements: opts,
                  totalElements: opts.length,
                  isCached: false
                })
              })
          })
      }
    })
  }

  function loadSiteOptions(params: { sortDesc: boolean }): Promise<{
    elements: SelectOption[]
    totalElements: number
    isCached: boolean
  }> {
    return new Promise((resolve) => {
      const environmentId = values.current.environment
      if (!environmentId) {
        resolve({ elements: [], totalElements: 0, isCached: false })
        return
      }

      const cachedOptions = siteOptionsCached.current[environmentId]

      if (cachedOptions) {
        const opts = postprocessSiteOptions(cachedOptions, params)
        resolve({
          elements: opts,
          totalElements: opts.length,
          isCached: true
        })
      } else {
        const study = studyOptions.find((s) => s.value === values.current.study)
        const environment = environmentOptions.find((e) => e.value === environmentId)

        service.etmf
          .getFilterParamsForMdQuery(constant.etmf.masterData.type.COUNTRY, {
            trial: { fsiName: study?.label || '' },
            environment: { fsiName: environment?.label || '' }
          })
          .then((filterParams: any) => {
            api.etmf
              .getSiteMDsForUpdate(environmentId, filterParams, values.current.action === actionOptions.add, true)
              .then((data: any[]) => {
                siteOptionsCached.current[environmentId] = data
                const opts = postprocessSiteOptions(data, params)
                setSiteOptions(opts)
                resolve({
                  elements: opts,
                  totalElements: opts.length,
                  isCached: false
                })
              })
          })
      }
    })
  }

  function postprocessSiteOptions(data: any[], params: any) {
    const countriesAndSites: any[] = []
    const dataClone = [...data]
    if (params.sortDesc) {
      dataClone.reverse()
    }

    dataClone.forEach((countryAndSites) => {
      const country = countryAndSites.country
      const sites = countryAndSites.sites
      const siteIds = sites.map((site: any) => 'site' + site.id)

      const countryOb = {
        isCountry: true,
        id: 'country' + country.id,
        realId: country.id,
        name: country.name,
        siteIds: siteIds
      }

      countriesAndSites.push(countryOb)

      sortArray(sites, params.sortDesc).forEach((site: any) => {
        countriesAndSites.push({
          isSite: true,
          id: 'site' + site.id,
          realId: site.id,
          name: site.name,
          countryId: countryOb.id,
          siteIds: siteIds
        })
      })
    })

    return countriesAndSites
  }

  function sortArray(arr: any[], desc: boolean) {
    const sorted = _.sortBy(arr, 'name')
    if (desc) {
      sorted.reverse()
    }
    return sorted
  }

  async function submit(formValues: FormValues): Promise<any> {
    // Use the ref values which should be up-to-date
    const currentValues = values.current

    let actionName: string
    if (currentValues.level === levelOptions.countries) {
      actionName =
        currentValues.action === actionOptions.add
          ? 'ADMIN_ACTION_ETMF_USS_ADD_COUNTRY_TO_STUDY'
          : 'ADMIN_ACTION_ETMF_USS_REMOVE_COUNTRIES_FROM_STUDY'
    } else {
      actionName =
        currentValues.action === actionOptions.add
          ? 'ADMIN_ACTION_ETMF_USS_ADD_SITES_TO_STUDY'
          : 'ADMIN_ACTION_ETMF_USS_REMOVE_SITES_FROM_STUDY'
    }

    let selectedIds: number[] = []

    if (currentValues.level === levelOptions.countries) {
      selectedIds = currentValues.countries.map((id: string) => parseInt(id))
    } else {
      const selectedSites = siteOptions.filter((site) => site.isSite && currentValues.sites.includes(site.id))
      selectedIds = selectedSites.map((i) => i.realId)
    }

    // Handle e-signature if required
    let eSignatureValidation: any = null

    if (context.service.auth.requireESignatureForFileCommands()) {
      try {
        // Request e-signature authentication
        const validation = await signAction('ADMIN_ACTION', actionName)

        // Extract only the fields needed by the backend API
        // For SSO/OKTA: backend expects only {relayState}
        // For DSO: backend expects only {username, password}
        if (validation.relayState) {
          eSignatureValidation = { relayState: validation.relayState }
        } else {
          eSignatureValidation = {
            username: validation.username || '',
            password: validation.password || ''
          }
        }
      } catch (error) {
        // User cancelled e-signature or authentication failed
        return
      }
    }

    // Execute the API call with validation
    try {
      let apiCall: Promise<any>

      if (currentValues.action === actionOptions.remove) {
        apiCall = api.etmf.deleteFile(selectedIds, eSignatureValidation)
      } else {
        const request: any = {
          productMasterDatafsiId: currentValues.product,
          trialMasterDatafsiId: currentValues.study,
          environmentMasterDatafsiId: currentValues.environment
        }

        if (currentValues.level === levelOptions.sites) {
          request.sitesMasterDatafsiIds = selectedIds
        } else {
          request.countryMasterDatafsiIds = selectedIds
        }

        apiCall = api.etmf.addMDtoMD(
          currentValues.level === levelOptions.sites ? MasterDataType.SITE : MasterDataType.COUNTRY,
          request,
          eSignatureValidation
        )
      }

      await apiCall
      closeModal()
      context.service.toast.success('UPDATE_ETMF_FOLDER_STRUCTURE_MSG')
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'An error occurred'
      context.service.toast.error(errorMsg)
      throw error
    }
  }

  const contextValue: DialogContextValue = {
    title,
    currentStep,
    isLoading,
    selection,

    actionOptions,
    levelOptions,
    productOptions,
    studyOptions,
    environmentOptions,

    loadStudyOptions,
    loadEnvironmentOptions,
    loadCountryOptions,
    loadSiteOptions,

    setCurrentValues: (vals: Partial<FormValues>, setValues: (values: FormValues) => void) => {
      const newValues = { ...values.current, ...vals }
      setValues(newValues)
      values.current = newValues
      return values.current
    },
    getCurrentValues: () => {
      return values.current
    },
    getDefaultValues: () => {
      return { ...defaultValues }
    },
    getStepName: (num: number) => {
      if (num === 1) return 'ETMF_UPDATE_STUDY_STRUCTURE_STEP1_TITLE'
      if (num === 2) return 'ETMF_UPDATE_STUDY_STRUCTURE_STEP2_TITLE'
      if (num === 3) return 'ETMF_UPDATE_STUDY_STRUCTURE_STEP3_TITLE'
      return null
    },
    goToNextStep: (vals: FormValues) => {
      values.current = vals
      setCurrentStep(currentStep + 1)
    },
    goToPreviousStep: () => {
      setCurrentStep(currentStep - 1)
    },
    submit
  }

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>
}
